import { ListBulletIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import { alignerTotalSuffix } from '../alignerTotal';
import { InstructionPill, LatePill } from '../ScanPills';
import { isComfortScan, scanDecision, scanTitle } from '../scanInstructionTone';
import { scanTakenAt } from '../scanTakenAt';
import { MonitoringStatusTitle } from '../StatusPills';
import type { DmScanSummary } from '../types';

export interface TimelineHeaderProps {
    scan: DmScanSummary | null;
    totalAligners: number | null;
    /** Undefined hides the status; null reads as Not started. */
    monitoringStatus?: string | null;
    currentAligner?: number | null;
    formatScanDate: (value: string) => string;
    hasSidePanel: boolean;
    showSidePanel: boolean;
    onToggleSidePanel?: () => void;
    /** False on a case with no aligners: drop the aligner count and the pill. */
    tracksAligners?: boolean;
    /** Host controls shown just before the Details toggle. */
    headerActions?: ReactNode;
    /** Rendered right after the Details toggle. */
    detailsMenu?: ReactNode;
}

type TimelineTitleProps = Pick<
    TimelineHeaderProps,
    | 'scan'
    | 'totalAligners'
    | 'monitoringStatus'
    | 'currentAligner'
    | 'formatScanDate'
    | 'tracksAligners'
>;

function TimelineTitle({
    scan,
    totalAligners,
    monitoringStatus,
    currentAligner = null,
    formatScanDate,
    tracksAligners = true
}: TimelineTitleProps) {
    const title = scan ? scanTitle(scan, tracksAligners) : 'No scans yet';
    return (
        <span className="min-w-0 truncate text-sm font-bold text-gray-800">
            {monitoringStatus === undefined ? (
                title
            ) : (
                <MonitoringStatusTitle
                    status={monitoringStatus}
                    current={currentAligner}
                    total={totalAligners}
                >
                    {title}
                </MonitoringStatusTitle>
            )}
            {scan && (
                <span className="font-semibold text-gray-500">
                    {tracksAligners && !isComfortScan(scan) && alignerTotalSuffix(totalAligners)}
                    {` · ${formatScanDate(scanTakenAt(scan))}`}
                </span>
            )}
        </span>
    );
}

function TimelineHeader({
    scan,
    totalAligners,
    monitoringStatus,
    currentAligner,
    formatScanDate,
    hasSidePanel,
    showSidePanel,
    onToggleSidePanel,
    tracksAligners = true,
    headerActions,
    detailsMenu
}: TimelineHeaderProps) {
    return (
        <div className="flex min-h-[20px] shrink-0 flex-wrap items-center justify-between gap-2">
            <TimelineTitle
                scan={scan}
                totalAligners={totalAligners}
                monitoringStatus={monitoringStatus}
                currentAligner={currentAligner}
                formatScanDate={formatScanDate}
                tracksAligners={tracksAligners}
            />

            <div className="flex shrink-0 items-center gap-1.5">
                {scan && (
                    <>
                        {tracksAligners && <InstructionPill decision={scanDecision(scan)} />}
                        <LatePill late={scan.late} daysLate={scan.daysLate} />
                    </>
                )}
                {headerActions}
                {hasSidePanel && (
                    <button
                        type="button"
                        onClick={onToggleSidePanel}
                        aria-pressed={showSidePanel}
                        title={showSidePanel ? 'Hide details' : 'Show details'}
                        className={`inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold transition-colors duration-200 ${
                            showSidePanel
                                ? 'border-gray-300 bg-gray-100 text-gray-700'
                                : 'border-gray-200 bg-white text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <ListBulletIcon className="h-3.5 w-3.5" />
                        Details
                    </button>
                )}
                {detailsMenu}
            </div>
        </div>
    );
}

export { TimelineHeader };
